/**
 * Geometria da roda — espelho de pontuax-mobile/src/components/RoletaWheel/geometry.ts.
 *
 * O preview do admin PRECISA usar a mesma matemática do app: se as duas
 * divergirem, o operador configura a fatia 3 aqui e o cliente vê outra coisa lá.
 * Fórmulas fixadas em .planning/roleta/03-UX-ADMIN.md §4.3.
 */

/** Ponto na circunferência. `deg` 0 = topo (12h), sentido horário. */
export const polar = (cx: number, cy: number, r: number, deg: number) => {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

/** Caminho SVG de uma fatia (setor circular) entre `start` e `end` graus. */
export const slicePath = (cx: number, cy: number, r: number, start: number, end: number) => {
  const a = polar(cx, cy, r, start);
  const b = polar(cx, cy, r, end);
  return `M ${cx} ${cy} L ${a.x} ${a.y} A ${r} ${r} 0 ${end - start > 180 ? 1 : 0} 1 ${b.x} ${b.y} Z`;
};

/** Ângulo do centro da fatia `i` numa roda de `n` fatias. */
export const sliceCenter = (i: number, n: number) => (i * 360) / n + 180 / n;

/** Início e fim (graus) da fatia `i`. */
export const sliceBounds = (i: number, n: number) => ({
  start: (i * 360) / n,
  end: ((i + 1) * 360) / n,
});

// ─── Cor ──────────────────────────────────────────────────────────────────────

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

const clampByte = (value: number) => Math.max(0, Math.min(255, Math.round(value)));

/** Converte "#abc"/"#aabbcc" em [r,g,b]. Entrada inválida cai no cinza neutro. */
export const parseHex = (hex?: string | null): [number, number, number] => {
  if (!hex || !HEX.test(hex)) return [120, 105, 223];
  let raw = hex.replace('#', '');
  if (raw.length === 3) raw = raw.split('').map((c) => c + c).join('');
  return [
    parseInt(raw.slice(0, 2), 16),
    parseInt(raw.slice(2, 4), 16),
    parseInt(raw.slice(4, 6), 16),
  ];
};

export const toHex = ([r, g, b]: [number, number, number]) =>
  `#${[r, g, b].map((c) => clampByte(c).toString(16).padStart(2, '0')).join('')}`;

/** Interpola duas cores. `t`=0 devolve `a`, `t`=1 devolve `b`. */
export const mix = (a: string, b: string, t: number): string => {
  const [ar, ag, ab] = parseHex(a);
  const [br, bg, bb] = parseHex(b);
  return toHex([ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t]);
};

/** Luminância relativa aproximada (0 escuro, 1 claro). */
export const luminance = (hex: string): number => {
  const [r, g, b] = parseHex(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
};

/** Cor de texto legível sobre `hex`. */
export const readableInk = (hex: string): string => (luminance(hex) > 0.62 ? '#1e293b' : '#ffffff');

/**
 * Paleta automática: alterna tom claro e tom escuro derivados da cor do app da
 * rede, com uma deriva por índice para que fatias vizinhas nunca fiquem iguais
 * quando a contagem é ímpar.
 */
export const autoSegmentColors = (base: string, count: number): string[] => {
  const safeCount = Math.max(1, count);
  return Array.from({ length: safeCount }, (_, index) => {
    const drift = (index / safeCount) * 0.1;
    return index % 2 === 0
      ? mix(base, '#ffffff', 0.1 + drift)
      : mix(base, '#0b0b12', 0.3 - drift);
  });
};

/** Cor efetiva da fatia: a escolhida pelo operador ou a automática. */
export const segmentColor = (chosen: string | null | undefined, base: string, index: number, count: number): string =>
  chosen && HEX.test(chosen) ? chosen : autoSegmentColors(base, count)[index] ?? base;

// ─── Sorteio local (só do preview "Testar giro") ──────────────────────────────

export interface WeightedSlice {
  weight: number;
  isEnabled: boolean;
}

/**
 * Sorteio ponderado local. Serve APENAS para o botão "Testar giro" do preview —
 * o sorteio de verdade é do servidor (`POST .../spin`), com RNG e estoque.
 * Devolve -1 quando não há fatia elegível.
 */
export const drawWeightedIndex = (slices: WeightedSlice[], random: () => number = Math.random): number => {
  const eligible = slices
    .map((slice, index) => ({ index, weight: Math.max(0, slice.isEnabled ? slice.weight : 0) }))
    .filter((slice) => slice.weight > 0);

  const total = eligible.reduce((sum, slice) => sum + slice.weight, 0);
  if (!eligible.length || total <= 0) return -1;

  let roll = random() * total;
  for (const slice of eligible) {
    roll -= slice.weight;
    if (roll <= 0) return slice.index;
  }
  return eligible[eligible.length - 1].index;
};

/**
 * Rotação final da roda para que a fatia `index` pare sob o ponteiro do topo.
 * Acumula sobre a rotação atual (framer-motion anima do valor corrente) e cai
 * fora do centro exato — parar sempre no meio parece programado.
 */
export const landingRotation = (
  currentRotation: number,
  index: number,
  count: number,
  turns = 5,
  jitterRatio = 0.55,
  random: () => number = Math.random,
): number => {
  const slice = 360 / Math.max(1, count);
  const jitter = (random() - 0.5) * slice * jitterRatio;
  const completedTurns = currentRotation - (((currentRotation % 360) + 360) % 360);
  return completedTurns + turns * 360 - sliceCenter(index, count) + jitter;
};
